import React, { ReactNode, MouseEvent, useState } from 'react';
import { Box, Typography, Collapse, Menu, MenuItem, useTheme } from '@mui/material';
import IconButton from './IconButton'; // bouton custom
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { useBoolean } from '../../hooks/useBoolean';
import { useSystemConfig } from '../../hooks/useSystemConfig';
import { systemConfigKeyEnum } from '../../utils/enums';
import { formatNumber } from '../../utils/numbers';

interface ReportItemCardProps {
    title?: string;
    titleComponent?: ReactNode;
    children?: ReactNode;
    onEdit?: () => void;
    onDelete?: () => void;
    extraActions?: ReactNode;
    subTitle?: string;
    disabled?: boolean;
    price?: number;
    disabledOnDelete?: boolean;
    disabledOnEdit?: boolean;
    majorAction?: ReactNode;
    extraInfo?: ReactNode;
    hidePrice?: boolean;
    hideMajorAction?: boolean;
    error?: boolean;
}

const ReportItemCard: React.FC<ReportItemCardProps> = ({
    title,
    titleComponent,
    children,
    onEdit,
    onDelete,
    extraActions,
    subTitle,
    disabled,
    price,
    disabledOnDelete,
    disabledOnEdit,
    majorAction,
    extraInfo,
    hidePrice = false,
    hideMajorAction = false,
    error = false,
}) => {
    const { t } = useTranslation();
    const openDetails = useBoolean(false);
    const { getConfigValue } = useSystemConfig();
    const theme = useTheme();

    const handleEdit = (e?: MouseEvent) => {
        e?.stopPropagation();
        onEdit && onEdit();
    };
    const handleDelete = (e?: MouseEvent) => {
        e?.stopPropagation();
        if (onDelete && window.confirm(t('confirmDeleteValidation'))) onDelete();
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column' },
                alignItems: { xs: 'stretch' },
                justifyContent: 'space-between',
                width: '100%',
                border: { xs: `1px solid ${theme.palette.divider}`, md: 'none' },
                borderRadius: 1,
                overflow: 'hidden',
                my: { xs: 0.5 },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch' },
                    gap: 0.5,
                    width: '100%',
                    borderRadius: { xs: 0, md: 1 },
                    '&:hover': {
                        bgcolor: children ? (theme) => theme.palette.action.hover : undefined,
                    },
                }}
                onClick={children ? openDetails.toggle : undefined}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: children ? 'pointer' : 'default',
                        flex: 1,
                        borderRadius: 1,
                        minHeight: 30,
                        p: 0.5,
                        minWidth: 0,
                    }}
                >
                    <Icon
                        name={openDetails.value ? 'keyboardArrowDownSharp' : 'keyboardArrowRightSharp'}
                        color={error ? 'error' : disabled || !children ? 'disabled' : 'primary'}
                    />

                    {titleComponent}

                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden', flex: 1 }}>
                        <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {title}
                        </Typography>

                        {subTitle && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {subTitle}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 1, sm: 2 },
                            flexDirection: 'row',
                        }}
                    >
                        {extraInfo && <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>{extraInfo}</Box>}
                        {!hidePrice && (
                            <Typography
                                variant="subtitle2"
                                color={price !== undefined ? 'warning' : 'error'}
                                fontWeight={600}
                                sx={{
                                    ml: 'auto',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                }}
                            >
                                {price !== undefined ? formatNumber(price, t('locale')) : '???'} {getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: { xs: 1, sm: 0 },
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: { xs: 'space-between', sm: 'flex-end' },
                        mt: { xs: 0.25, sm: 0 },
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            minWidth: 0,
                            mr: { xs: 0, sm: 1 },
                        }}
                    >
                        {!hideMajorAction && majorAction}
                    </Box>

                    {!disabled && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, minWidth: 100 }}>
                            {extraActions}
                            {onEdit && (
                                <IconButton
                                    iconName="edit"
                                    size="small"
                                    onClick={(e: MouseEvent) => {
                                        e.stopPropagation();
                                        handleEdit(e);
                                    }}
                                    disabled={disabledOnEdit}
                                />
                            )}
                            {onDelete && (
                                <IconButton
                                    iconName="deleteForever"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(e);
                                    }}
                                    color="error"
                                    disabled={disabledOnDelete}
                                />
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
            <Collapse in={openDetails.value}>
                <Box
                    sx={{
                        borderRadius: { xs: 0, md: 1 },
                        p: 1,
                        bgcolor: 'background.default',
                        display: 'flex',
                        gap: 0.5,
                        flexDirection: 'column',
                        mt: { xs: 0, md: 0.5 },
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
};

export default ReportItemCard;
